import { forwardRef, useCallback, useMemo } from 'react';
import {
    NumericFormat,
    PatternFormat,
    useNumericFormat,
} from 'react-number-format';

import { InputIcon } from '@perpay-web/components/base//InputIcon/InputIcon';
import { InputError } from '@perpay-web/components/base/InputError/InputError';
import { InputLabel } from '@perpay-web/components/base/InputLabel/InputLabel';
import { InputPrefix } from '@perpay-web/components/base/InputPrefix/InputPrefix';
import { useEndToEndTestId } from '@perpay-web/hooks/useEndToEndTestId';
import { getClassName } from '@perpay-web/utils/domUtils';

import styles from '@perpay-web/design/styles/inputs.scss';

export const TextFieldV2 = forwardRef((props, ref) => {
    const {
        value = '',
        name,
        className = '',
        type,
        placeholder,
        label,
        disabled = false,
        format,
        mask,
        touched = false,
        errors = null,
        updateWithFormatting = false,
        loading = false,
        onChange,
        prefix,
        formatType = 'pattern',
        thousandSeparator = ',',
        allowLeadingZeros = false,
        allowNegative = false,
        decimalScale = null,
        ...rest
    } = props;

    const { format: baseFormat, removeFormatting } = useNumericFormat(props);
    const endToEndTestIDParams = useEndToEndTestId();

    const options = useMemo(
        () =>
            rest.options && (
                <>
                    {rest.includeEmptyOption ? (
                        <option disabled aria-label='Empty' />
                    ) : undefined}
                    {rest.options.map((optionItem) => (
                        <option value={optionItem.value} key={optionItem.value}>
                            {optionItem.label}
                        </option>
                    ))}
                </>
            ),
        [rest.includeEmptyOption, rest.options],
    );

    const handleOnChange = useCallback(
        (e) => {
            const valueToUpdate = updateWithFormatting
                ? e.target.value
                : e.target.value.replace(/\D*/g, '');

            if (typeof onChange === 'function') {
                onChange(e);
            }

            if (rest.setFieldValue) {
                rest.setFieldValue(name, valueToUpdate);
            }
        },
        [name, onChange, rest, updateWithFormatting],
    );

    const [Component, componentProps] = useMemo(() => {
        if (rest.options) {
            return ['select', { children: options, onChange }];
        }

        if (formatType === 'numeric') {
            let numericFormatter = format;
            if (typeof format === 'function') {
                numericFormatter = (val) => {
                    const formattedVal = baseFormat(val);
                    return format(formattedVal);
                };
            }
            return [
                NumericFormat,
                {
                    mask,
                    format: numericFormatter,
                    removeFormatting,
                    thousandSeparator,
                    allowLeadingZeros,
                    allowNegative,
                    decimalScale,
                    getInputRef: ref,
                    onChange: handleOnChange,
                },
            ];
        }

        if (mask || format) {
            return [
                PatternFormat,
                {
                    mask,
                    format,
                    getInputRef: ref,
                    onChange: handleOnChange,
                },
            ];
        }

        return ['input', { ref, onChange }];
    }, [
        allowLeadingZeros,
        allowNegative,
        baseFormat,
        decimalScale,
        format,
        formatType,
        handleOnChange,
        mask,
        onChange,
        options,
        ref,
        removeFormatting,
        rest.options,
        thousandSeparator,
    ]);

    const isError = useMemo(
        () => errors && touched && errors[0],
        [errors, touched],
    );

    return (
        <div
            className={getClassName(
                className,
                styles['text-field-row'],
                isError && styles['text-field-row--error'],
            )}
        >
            <div className={getClassName(styles['text-field-container'])}>
                <Component
                    value={value}
                    name={name}
                    type={type}
                    placeholder={placeholder}
                    disabled={disabled}
                    autoComplete='off'
                    data-lpignore='true'
                    {...componentProps}
                    {...endToEndTestIDParams}
                    {...rest}
                    className={getClassName(
                        styles['text-field'],
                        value && styles['text-field--filled'],
                        label && styles['text-field--labeled'],
                    )}
                />
                <InputLabel label={label} />
                <InputPrefix prefix={prefix} />
                <InputIcon
                    type={rest.options ? 'select' : type}
                    loading={loading}
                />
            </div>
            {isError ? <InputError name={name} error={errors[0]} /> : null}
        </div>
    );
});

TextFieldV2.displayName = 'TextFieldV2';
